/**
 * free-gift-cart.js
 * -----------------
 * State-driven free-gift mechanic for the Purity cart drawer.
 *
 * Core invariant:
 *   eligible_gifts = sum of trigger-product quantities in cart
 *   existing_gifts = sum of gift-line quantities (marked _free_gift)
 *
 *   If existing < eligible  → show picker (one slot at a time)
 *   If existing > eligible  → remove excess gift units
 *   If existing === eligible → hide picker
 *
 * This means:
 *  - Only one free gift per rule regardless of trigger quantity.
 *  - Removing all trigger items removes the gift automatically.
 *  - If the gift is already in cart, the picker stays hidden.
 *  - Clicks use event delegation so re-renders never lose listeners.
 */

(function () {
  'use strict';

  // ─── Constants ──────────────────────────────────────────────────────────────

  const GIFT_PROPERTY     = '_free_gift';
  const GIFT_FOR_PROPERTY = '_gift_for';
  const PICKER_ID         = 'cart-gift-picker';
  const WARNING_ID        = 'cart-gift-warning';
  const CHECKOUT_FORM_ID  = 'CartDrawer-CheckoutForm';

  // ─── Helpers ────────────────────────────────────────────────────────────────

  function getRules() {
    const el = document.getElementById('free-gift-rules-data');
    if (!el) return null;
    try { return JSON.parse(el.textContent); }
    catch (e) { console.error('[FreeGift] Could not parse rules JSON', e); return null; }
  }

  function money(cents) {
    if (typeof window.Shopify !== 'undefined' && window.Shopify.money_format) {
      return window.Shopify.money_format.replace('{{amount}}', (cents / 100).toFixed(2));
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function fetchCart() {
    return fetch('/cart.js', {
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    }).then((r) => r.json());
  }

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function getSections() {
    const drawer = getCartDrawer();
    return drawer ? drawer.getSectionsToRender().map((s) => s.id || s.section) : [];
  }

  function rerenderDrawer(sectionsData) {
    const drawer = getCartDrawer();
    if (!drawer || !sectionsData) return;
    drawer.renderContents({ sections: sectionsData });
    document.dispatchEvent(new CustomEvent('shopify:section:load'));
  }

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function colorNameToHex(name) {
    const map = {
      black:'#1a1a1a', white:'#f5f5f5', red:'#dc2626', blue:'#2563eb',
      green:'#16a34a', yellow:'#eab308', pink:'#ec4899', purple:'#9333ea',
      orange:'#ea580c', grey:'#9ca3af', gray:'#9ca3af', brown:'#92400e',
      navy:'#1e3a5f', beige:'#d4b896', cream:'#fef3c7', gold:'#d97706',
      silver:'#c0c0c0', tan:'#d2b48c', olive:'#808000', coral:'#ff6b6b',
      teal:'#0d9488', mint:'#6ee7b7', lavender:'#c4b5fd', rose:'#f43f5e',
    };
    return map[(name || '').toLowerCase().trim()] || '#e5e7eb';
  }

  // ─── Quantity helpers ────────────────────────────────────────────────────────

  /**
   * Total quantity of trigger items for a rule in the current cart.
   * Gift lines (marked _free_gift) are excluded so that a free gift of the
   * same product does not count as an additional trigger.
   */
  function getTriggerQty(rule, cartItems) {
    return cartItems
      .filter(
        (item) =>
          item.product_id === rule.trigger_id &&
          !(item.properties && item.properties[GIFT_PROPERTY])
      )
      .reduce((sum, item) => sum + item.quantity, 0);
  }

  /**
   * Total quantity of free-gift lines for a rule in the current cart.
   * Counts both items added via the picker (_free_gift property) and items
   * already discounted to $0 by Shopify automatic discounts (BOGO).
   */
  function getGiftQty(rule, cartItems) {
    return cartItems
      .filter(
        (item) =>
          item.product_id === rule.gift_id &&
          (
            (item.properties && item.properties[GIFT_PROPERTY]) ||
            item.final_price === 0
          )
      )
      .reduce((sum, item) => sum + item.quantity, 0);
  }

  /**
   * How many more gift slots need to be filled for this rule.
   * Positive → picker should show. Zero → all filled. Clamped to 0 minimum.
   */
  function slotsNeeded(rule, cartItems) {
    const triggerQty = Math.min(getTriggerQty(rule, cartItems), 1);
    return Math.max(0, triggerQty - getGiftQty(rule, cartItems));
  }

  // ─── Sync gift quantities ─────────────────────────────────────────────────────
  //
  // Called before rendering the picker. Removes excess gift units when the
  // customer reduces their trigger quantity (e.g. removes a bag or decreases qty).
  // Returns true if any changes were made (caller should bail and let the
  // observer trigger a fresh refresh).

  let _isSyncingGifts = false; // guard against fetch-interceptor re-entry

  async function syncGiftQuantities(config, cartItems) {
    if (!config || !config.enabled) return false;

    const drawer  = getCartDrawer();
    const sections = getSections();
    let changesMade = false;

    for (const rule of config.rules) {
      const triggerQty = Math.min(getTriggerQty(rule, cartItems), 1);

      // Only picker-added gift lines can be removed by sync
      const giftLines = cartItems.filter(
        (item) =>
          item.product_id === rule.gift_id &&
          item.properties &&
          item.properties[GIFT_PROPERTY]
      );
      const giftQty = giftLines.reduce((s, i) => s + i.quantity, 0);
      let excess = giftQty - triggerQty; // how many units to remove
      if (excess <= 0) continue;

      // Remove from the newest gift lines first
      _isSyncingGifts = true;
      try {
        for (const giftLine of [...giftLines].reverse()) {
          if (excess <= 0) break;
          const removeQty = Math.min(giftLine.quantity, excess);
          excess -= removeQty;
          const newQty = giftLine.quantity - removeQty;
          changesMade = true;

          try {
            const res = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: giftLine.key,
                quantity: newQty,
                sections,
                sections_url: window.location.pathname,
              }),
            });
            const data = await res.json();
            if (data.sections && drawer) {
              // Force a full drawer body replace so the empty-cart state renders correctly
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = data.sections[drawer.sectionId] || '';
              const newBody = tempDiv.querySelector('.drawer__body');
              const currentBody = drawer.querySelector('.drawer__body');
              if (newBody && currentBody) {
                currentBody.innerHTML = newBody.innerHTML;
              }
              // Also update cart icon
              if (data.sections['cart-icon-bubble']) {
                const iconEl = document.getElementById('cart-icon-bubble');
                if (iconEl) {
                  const iconDiv = document.createElement('div');
                  iconDiv.innerHTML = data.sections['cart-icon-bubble'];
                  const newIcon = iconDiv.querySelector('#cart-icon-bubble');
                  if (newIcon) iconEl.innerHTML = newIcon.innerHTML;
                }
              }
            }
          } catch (e) {
            console.error('[FreeGift] syncGiftQuantities error', e);
          }
        }
      } finally {
        _isSyncingGifts = false;
      }
    }

    return changesMade;
  }

  // ─── Picker rendering ────────────────────────────────────────────────────────

  function renderPicker(config, cartItems) {
    const container = document.getElementById(PICKER_ID);
    if (!container) return;

    const rulesNeedingPicker = config.rules.filter((r) => slotsNeeded(r, cartItems) > 0);

    if (rulesNeedingPicker.length === 0) {
      if (container.style.display !== 'none') {
        container.style.display = 'none';
        container.innerHTML = '';
        container.removeAttribute('data-picker-state');
      }
      return;
    }

    // Build a state key from which rules need picking and their current counts.
    // If the key matches what's already rendered, skip the innerHTML replacement —
    // this prevents the gift image from flickering on repeated refreshPicker calls
    // that arrive with no state change (e.g. open-observer + content-observer both
    // firing within milliseconds of each other when the drawer opens).
    const stateKey = rulesNeedingPicker
      .map((r) => `${r.gift_id}:${getTriggerQty(r, cartItems)}:${getGiftQty(r, cartItems)}`)
      .join('|');

    container.style.setProperty('--fgc-accent',  config.accent_color  || '#059669');
    container.style.setProperty('--fgc-warning', config.warning_color || '#DC2626');
    container.style.display = '';

    if (container.dataset.pickerState === stateKey) return; // nothing changed — leave DOM alone
    container.dataset.pickerState = stateKey;

    container.innerHTML = rulesNeedingPicker
      .map((rule) => renderRuleRow(rule, cartItems))
      .join('');

    // Hover labels for swatches (clicks handled via delegation on container)
    rulesNeedingPicker.forEach((rule) => attachSwatchHover(rule));
  }

  function renderRuleRow(rule, cartItems) {
    const triggerQty = Math.min(getTriggerQty(rule, cartItems), 1);
    const giftQty    = getGiftQty(rule, cartItems);
    const slotLabel  = '';

    const imgSrc  = rule.gift_featured_image
      ? rule.gift_featured_image + (rule.gift_featured_image.includes('?') ? '&' : '?') + 'width=280'
      : null;
    const imgHtml = imgSrc
      ? `<img src="${imgSrc}" alt="${escapeHtml(rule.gift_title || '')}" class="fgc-pick-image" width="112" height="112" loading="lazy">`
      : '';

    const freePriceHtml = rule.gift_price
      ? `<span class="fgc-price-normal"><s>(normally ${money(rule.gift_price)})</s></span>`
      : '';
    const priceLine = `
      <div class="fgc-price-line">
        <span class="fgc-free-pill">FREE</span>
        <span class="fgc-price-free">${money(0)}</span>
        ${freePriceHtml}
      </div>`;

    const productName = rule.gift_title
      ? `<span class="fgc-product-name">${escapeHtml(rule.gift_title)}</span>`
      : '';

    let controlHtml = '';
    if (rule.gift_has_only_default_variant || !rule.gift_variants || rule.gift_variants.length <= 1) {
      controlHtml = `
        <button
          type="button"
          class="fgc-claim-btn"
          data-rule-gift-id="${rule.gift_id}"
          aria-label="Add free gift to cart"
        >Claim your free gift</button>`;
    } else {
      const swatches = rule.gift_variants.map((v) => {
        const imgUrl = v.image
          ? v.image + (v.image.includes('?') ? '&' : '?') + 'width=80'
          : null;
        const bgStyle = imgUrl
          ? `background-image:url('${imgUrl}');background-size:cover;background-position:center;`
          : `background-color:${colorNameToHex(v.option1 || v.title)};`;
        return `<button
          type="button"
          class="fgc-swatch-btn${!v.available ? ' fgc-swatch-btn--unavailable' : ''}"
          data-variant-id="${v.id}"
          data-rule-gift-id="${rule.gift_id}"
          data-variant-title="${escapeHtml(v.title)}"
          ${!v.available ? 'disabled aria-disabled="true"' : ''}
          aria-label="${escapeHtml(v.title)}"
          title="${escapeHtml(v.title)}"
          style="${bgStyle}"
        ></button>`;
      }).join('');
      controlHtml = `
        <div class="fgc-swatch-row">
          <span class="fgc-variant-label">Pick your color: </span>
          <span class="fgc-swatch-selected-name"></span>
        </div>
        <div class="fgc-variant-picker" data-rule-gift-id="${rule.gift_id}">
          ${swatches}
        </div>`;
    }

    return `
      <div class="fgc-reminder-box" data-rule-gift-id="${rule.gift_id}">
        <div class="fgc-reminder-left">
          <svg class="fgc-gift-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="20 12 20 22 4 22 4 12"></polyline>
            <rect x="2" y="7" width="20" height="5"></rect>
            <line x1="12" y1="22" x2="12" y2="7"></line>
            <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path>
            <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path>
          </svg>
          <div class="fgc-reminder-text">
            <p class="fgc-reminder-heading">Claim Your FREE BOGO item${slotLabel}</p>
            ${priceLine}
            ${productName}
            ${controlHtml}
          </div>
        </div>
        ${imgHtml}
      </div>`;
  }

  /** Hover label on swatches — non-critical, attached directly after innerHTML set. */
  function attachSwatchHover(rule) {
    const container = document.getElementById(PICKER_ID);
    if (!container) return;
    const picker = container.querySelector(`.fgc-variant-picker[data-rule-gift-id="${rule.gift_id}"]`);
    if (!picker) return;
    const nameEl = picker.previousElementSibling?.querySelector('.fgc-swatch-selected-name');
    if (!nameEl) return;

    picker.querySelectorAll('.fgc-swatch-btn:not([disabled])').forEach((btn) => {
      btn.addEventListener('mouseenter', () => {
        nameEl.textContent = btn.dataset.variantTitle || '';
      });
      btn.addEventListener('mouseleave', () => {
        const sel = picker.querySelector('.fgc-swatch-btn--selected');
        nameEl.textContent = sel ? sel.dataset.variantTitle || '' : '';
      });
    });
  }

  // ─── Event delegation ────────────────────────────────────────────────────────
  //
  // A single click listener on #cart-gift-picker survives innerHTML replacements
  // of the picker's children. Re-attached once per refreshPicker (because the
  // picker element itself is replaced by renderContents after every cart change).

  function setupPickerDelegation(config) {
    const container = document.getElementById(PICKER_ID);
    if (!container || container._fgcDelegated) return;
    container._fgcDelegated = true;

    container.addEventListener('click', (e) => {
      // ── Swatch button ──
      const swatchBtn = e.target.closest('.fgc-swatch-btn');
      if (swatchBtn) {
        if (swatchBtn.disabled || swatchBtn.classList.contains('fgc-swatch-btn--unavailable')) return;
        e.stopPropagation();

        const variantId = parseInt(swatchBtn.dataset.variantId, 10);
        const giftId    = parseInt(swatchBtn.dataset.ruleGiftId, 10);
        if (!variantId || !giftId) return;

        // Immediate visual feedback
        const picker = swatchBtn.closest('.fgc-variant-picker');
        if (picker) {
          picker.querySelectorAll('.fgc-swatch-btn').forEach((b) => {
            b.disabled = true;
            b.setAttribute('aria-disabled', 'true');
            b.classList.remove('fgc-swatch-btn--selected');
          });
          swatchBtn.classList.add('fgc-swatch-btn--selected');
          const nameEl = picker.previousElementSibling?.querySelector('.fgc-swatch-selected-name');
          if (nameEl) nameEl.textContent = swatchBtn.dataset.variantTitle || '';
        }

        const rule = config.rules.find((r) => r.gift_id === giftId);
        if (rule) addGiftToCart(variantId, rule, config);
        return;
      }

      // ── Claim button (single-variant) ──
      const claimBtn = e.target.closest('.fgc-claim-btn');
      if (claimBtn) {
        if (claimBtn.disabled || claimBtn.classList.contains('loading')) return;
        e.stopPropagation();

        const giftId = parseInt(claimBtn.dataset.ruleGiftId, 10);
        const rule   = config.rules.find((r) => r.gift_id === giftId);
        if (!rule) return;
        const variantId = rule.gift_variants?.[0]?.id;
        if (!variantId) return;

        claimBtn.disabled = true;
        claimBtn.classList.add('loading');
        addGiftToCart(variantId, rule, config);
      }
    });
  }

  // ─── Add gift to cart ────────────────────────────────────────────────────────

  function addGiftToCart(variantId, rule, config) {
    const drawer   = getCartDrawer();
    const sections = getSections();

    fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: {
          [GIFT_PROPERTY]: true,
          [GIFT_FOR_PROPERTY]: rule.trigger_handle || '',
        },
        sections,
        sections_url: window.location.pathname,
      }),
    })
      .then((r) => r.json())
      .then((response) => {
        if (response.status) {
          // Shopify returned an error (e.g. out of stock)
          console.error('[FreeGift] Failed to add gift:', response.description);
          refreshPicker(config);
          return;
        }
        if (response.sections && drawer) {
          rerenderDrawer(response.sections);
        }
        // Re-evaluate: if more slots remain (multi-bag), picker re-opens automatically
        refreshPicker(config);
      })
      .catch((e) => {
        console.error('[FreeGift] addGiftToCart error', e);
        refreshPicker(config);
      });
  }

  // ─── Checkout gating ─────────────────────────────────────────────────────────

  function setupCheckoutGate(config, cartItems) {
    const form    = document.getElementById(CHECKOUT_FORM_ID);
    const warning = document.getElementById(WARNING_ID);
    if (!form || !warning) return;

    if (form._fgcGateListener) {
      form.removeEventListener('submit', form._fgcGateListener);
    }

    form._fgcGateListener = function (e) {
      // Block if any required rule has unfilled slots
      const missingRule = config.rules.find(
        (rule) =>
          rule.required &&
          getTriggerQty(rule, cartItems) > 0 &&
          slotsNeeded(rule, cartItems) > 0
      );
      if (missingRule) {
        e.preventDefault();
        e.stopImmediatePropagation();
        warning.textContent = config.warning || 'Please select your free gift before checking out.';
        warning.style.setProperty('--fgc-warning', config.warning_color || '#DC2626');
        warning.style.display = '';
        const picker = document.getElementById(PICKER_ID);
        if (picker && picker.style.display !== 'none') {
          picker.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        } else {
          warning.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    };
    form.addEventListener('submit', form._fgcGateListener);

    // Auto-hide the warning once all slots are filled
    const anyMissing = config.rules.some(
      (rule) =>
        rule.required &&
        getTriggerQty(rule, cartItems) > 0 &&
        slotsNeeded(rule, cartItems) > 0
    );
    if (!anyMissing) {
      warning.style.display = 'none';
      warning.textContent   = '';
    }
  }

  // ─── Main refresh ─────────────────────────────────────────────────────────────

  async function refreshPicker(config) {
    if (!config || !config.enabled) return;

    const cart      = await fetchCart();
    const cartItems = cart.items || [];

    // Remove excess gift units first. If changes were made, the drawer re-renders
    // and the MutationObserver fires refreshPicker again with fresh state.
    const changesMade = await syncGiftQuantities(config, cartItems);
    if (changesMade) return;

    // Set up delegated click handling on the (possibly new) picker element
    setupPickerDelegation(config);

    // Render picker and wire checkout gate
    renderPicker(config, cartItems);
    setupCheckoutGate(config, cartItems);
  }

  // ─── Observe cart drawer mutations ───────────────────────────────────────────

  function observeDrawerRerenders(config) {
    const drawer = getCartDrawer();
    if (!drawer) return;

    let debounceTimer;
    const observer = new MutationObserver((mutations) => {
      // Ignore mutations we caused inside the picker / warning elements
      const picker  = document.getElementById(PICKER_ID);
      const warning = document.getElementById(WARNING_ID);
      const isOwnUpdate = mutations.every((m) => {
        const t = m.target;
        return (
          t === picker  || (picker  && picker.contains(t))  ||
          t === warning || (warning && warning.contains(t))
        );
      });
      if (isOwnUpdate) return;

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (document.getElementById(PICKER_ID)) refreshPicker(config);
      }, 150);
    });

    observer.observe(drawer, { childList: true, subtree: true });
  }

  // ─── Fetch interceptor ─────────────────────────────────────────────────────
  //
  // Intercept fetch calls to /cart/change.js and /cart/update.js so that gift
  // sync runs reliably after every cart modification — not only when the
  // MutationObserver happens to detect a re-render.

  function interceptCartFetch(config) {
    const _originalFetch = window.fetch;
    window.fetch = function (...args) {
      const result = _originalFetch.apply(this, args);
      // Skip if we are the ones modifying the cart (syncGiftQuantities)
      if (_isSyncingGifts) return result;

      const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      if (url.includes('/cart/change') || url.includes('/cart/update')) {
        result
          .then((r) => r.clone().json())
          .then(() => {
            // Give the theme time to process its own re-render first
            setTimeout(() => refreshPicker(config), 350);
          })
          .catch(() => {});
      }
      return result;
    };
  }

  // ─── Bootstrap ───────────────────────────────────────────────────────────────

  function init() {
    const config = getRules();
    if (!config || !config.enabled || !config.rules || config.rules.length === 0) return;

    // Drop rules missing trigger or gift product
    config.rules = config.rules.filter((r) => r.trigger_id && r.gift_id);
    if (config.rules.length === 0) return;

    // Intercept cart changes to reliably trigger gift sync
    interceptCartFetch(config);

    // Refresh when cart icon is clicked (drawer opening)
    document.addEventListener('click', (e) => {
      if (e.target.closest('#cart-icon-bubble, #cart-icon-bubble-mobile')) {
        setTimeout(() => refreshPicker(config), 100);
      }
    });

    // Refresh when drawer's 'active' class is added (e.g. after add-to-cart)
    const drawer = getCartDrawer();
    if (drawer) {
      new MutationObserver((mutations) => {
        mutations.forEach((m) => {
          if (m.type === 'attributes' && m.attributeName === 'class') {
            if (drawer.classList.contains('active')) {
              setTimeout(() => refreshPicker(config), 50);
            }
          }
        });
      }).observe(drawer, { attributes: true });
    }

    // Watch for theme-driven cart re-renders (qty changes, removals, etc.)
    observeDrawerRerenders(config);

    // Run immediately if picker is already in DOM (theme editor, page reload)
    if (document.getElementById(PICKER_ID)) refreshPicker(config);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
